import { resolveExportBackground, svgToPngBlob } from '../../../services/analysisExportService.js'
import type { ChartResult } from '../../../types/chart.js'

function escapeCsvCell(val: unknown): string {
  if (val === null || val === undefined) return ''
  const str = String(val)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Serializes a ChartResult into CSV text containing every series and data point.
 */
export function chartToCsvString(result: ChartResult): string {
  const header = ['Series', 'X', 'Y'].map(escapeCsvCell).join(',')
  const rows: string[] = [header]

  for (const s of result.series) {
    for (const p of s.points) {
      const line = [
        escapeCsvCell(s.label),
        escapeCsvCell(p.x),
        escapeCsvCell(p.y),
      ].join(',')
      rows.push(line)
    }
  }

  return rows.join('\n')
}

/**
 * Downloads a ChartResult as a CSV file.
 */
export function exportChartToCsv(result: ChartResult, filename = 'chart-data.csv'): void {
  const csvContent = chartToCsvString(result)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Exports a rendered chart SVG element to a PNG Blob using analysisExportService.
 */
export async function exportChartToPngBlob(svg: SVGElement, scale = 2): Promise<Blob> {
  const bg = resolveExportBackground()
  return svgToPngBlob(svg, scale, bg.hex)
}

/**
 * Downloads a rendered chart SVG element as a PNG image.
 */
export async function exportChartToPng(svg: SVGElement, filename = 'chart.png', scale = 2): Promise<void> {
  const blob = await exportChartToPngBlob(svg, scale)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename.endsWith('.png') ? filename : `${filename}.png`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
